import React, { useEffect, useState } from 'react';
import { useTheme } from '../utils/theme-context';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabase from '../integrations/supabase';
import apiService from '../services/api';
import { Tooltip } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StyleIcon from '@mui/icons-material/Style';
import QuizIcon from '@mui/icons-material/Quiz';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import NumberInputModal from '../components/NumberInputModal';
import FlashcardsModal from '../components/FlashcardsModal';
import QuizModal from '../components/QuizModal';
import ExistingContentModal from '../components/ExistingContentModal';
import RelearnContentCard from '../components/RelearnContentCard';
import CheckpointSystem from '../components/CheckpointSystem';
import ExamCountdown from '../components/ExamCountdown';
import { AnimatePresence, motion } from 'framer-motion';
import { DashboardSkeleton } from '../components/SkeletonLoader';

const Dashboard = () => {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState({ name: 'Learner', coins: 0, streak: 0, subjects: [], class: '' });
  const [showUnselected, setShowUnselected] = useState(false);
  const [addingSubject, setAddingSubject] = useState(null);
  const { themeTexts, themeIcons } = useTheme();
  const navigate = useNavigate();

  // Subject-level content state
  const [showNumberInput, setShowNumberInput] = useState(false);
  const [numberInputConfig, setNumberInputConfig] = useState({});
  const [showFlashcards, setShowFlashcards] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [flashcards, setFlashcards] = useState([]);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showRelearnCard, setShowRelearnCard] = useState(false);
  const [relearnCardData, setRelearnCardData] = useState({
    contentType: null,
    subject: null
  });
  
  // Existing content modal state
  const [showExistingContentModal, setShowExistingContentModal] = useState(false);
  const [existingContentData, setExistingContentData] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [studyPlan, setStudyPlan] = useState(null);
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState([]);

  useEffect(() => {
    const getDashboardData = async () => {
      if (authUser) {
        // Update user name from auth context
        const fullName = authUser.full_name || authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'Learner';
        setUser(prev => ({ ...prev, name: fullName }));
        
        // Get course selection from database
        const { data: courseSelection, error } = await supabase
          .from('course_selections')
          .select('*')
          .eq('user_id', authUser.id)
          .single();
        
        if (courseSelection && !error) {
          // Handle both old format (array of strings) and new format (array of objects)
          let userSubjects = [];
          let userSubjectData = [];
  
          
          if (courseSelection.selected_subjects && courseSelection.selected_subjects.length > 0) {
            if (typeof courseSelection.selected_subjects[0] === 'string') {
              // Check if strings are JSON objects
              userSubjects = courseSelection.selected_subjects.map(subject => {
                if (subject.startsWith('{') && subject.includes('subject_name')) {
                  try {
                    const parsed = JSON.parse(subject);
                    return parsed.subject_name;
                  } catch (error) {
                    console.warn('🔧 Failed to parse JSON subject:', error);
                    return subject;
                  }
                }
                return subject;
              });
              
              userSubjectData = courseSelection.selected_subjects.map(subject => {
                if (subject.startsWith('{') && subject.includes('subject_name')) {
                  try {
                    return JSON.parse(subject);
                  } catch (error) {
                    console.warn('🔧 Failed to parse JSON subject for data:', error);
                    return { subject_name: subject, book_name: '' };
                  }
                }
                return { subject_name: subject, book_name: '' };
              });
              
  
            } else {
              // New format: array of objects with subject_name and book_name
              userSubjects = courseSelection.selected_subjects.map(subject => subject.subject_name);
              userSubjectData = courseSelection.selected_subjects;

            }
          }
          
          
          
          setUser(prev => ({ 
            ...prev, 
            subjects: userSubjects,
            selectedSubjectData: userSubjectData, // Store parsed data for book names
            class: courseSelection.selected_class || '',
            stream: courseSelection.selected_stream || 'Science',
            coins: 50, 
            streak: 3 
          }));
          
          // Fetch available subjects for the user's class from database
          await fetchAvailableSubjects(courseSelection.selected_class);
          
          // Update localStorage for backward compatibility
          localStorage.setItem('user', JSON.stringify({
            name: courseSelection.full_name || fullName,
            class: courseSelection.selected_class,
            stream: courseSelection.selected_stream,
            subjects: userSubjects // Use the processed subject names, not the raw data
          }));
        } else {
  
          // Check localStorage as fallback
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
                          try {
                const parsedUser = JSON.parse(storedUser);
    
                
                // Clean subjects data - handle both strings and objects
                let cleanSubjects = [];
                let cleanSubjectData = [];
                
                if (parsedUser.subjects && parsedUser.subjects.length > 0) {
                  if (typeof parsedUser.subjects[0] === 'string') {
                    cleanSubjects = parsedUser.subjects;
                    cleanSubjectData = parsedUser.subjects.map(subject => ({ subject_name: subject, book_name: '' }));
                  } else {
                    cleanSubjects = parsedUser.subjects.map(subject => subject.subject_name || subject);
                    cleanSubjectData = parsedUser.subjects;
                  }
                }
                
                
                
                setUser(prev => ({ 
                  ...prev, 
                  subjects: cleanSubjects,
                  selectedSubjectData: cleanSubjectData,
                  class: parsedUser.class || '',
                  stream: parsedUser.stream || 'Science',
                  coins: 50, 
                  streak: 3 
                }));
            } catch (error) {
              console.error('🔧 Error parsing localStorage user data:', error);
              navigate('/course-selection');
            }
          } else {
            // If no course selection, redirect to course selection page
            navigate('/course-selection');
          }
        }
      }
      setLoading(false);
    };
    getDashboardData();
  }, [authUser, navigate]);

  // Function to fetch available subjects from database
  const fetchAvailableSubjects = async (className) => {
    try {
      // Extract class number from class name (e.g., "Class VI" -> 6, "Class 6" -> 6)
      let classNumber = null;
      
      if (className) {
        // First try to extract digits
        const digitMatch = className.match(/\d+/);
        if (digitMatch) {
          classNumber = parseInt(digitMatch[0]);
        } else {
          // Handle Roman numerals
          const romanMatch = className.match(/VI|VII|VIII|IX|X|XI|XII/i);
          if (romanMatch) {
            const romanToNumber = {
              'VI': 6, 'vi': 6,
              'VII': 7, 'vii': 7,
              'VIII': 8, 'viii': 8,
              'IX': 9, 'ix': 9,
              'X': 10, 'x': 10,
              'XI': 11, 'xi': 11,
              'XII': 12, 'xii': 12
            };
            classNumber = romanToNumber[romanMatch[0]];
          }
        }
      }
      
      
      
      if (!classNumber) {
        console.error('Could not extract class number from:', className);
        return;
      }

      // Fetch subjects for this class from database
      const { data: subjects, error } = await supabase
        .from('master_class_subject_books')
        .select('subject_name, book_name')
        .eq('class_id', classNumber)
        .order('subject_name');

      if (subjects && !error) {
        // Store full subject data with book names
        setAvailableSubjects(subjects);

      } else {
        console.error('Error fetching subjects:', error);
      }
    } catch (error) {
      console.error('Error fetching available subjects:', error);
    }
  };

  // Load study plan from localStorage
  useEffect(() => {
    const loadStudyPlan = () => {
      try {
        const savedPlan = localStorage.getItem('studyPlan');
        if (savedPlan) {
          setStudyPlan(JSON.parse(savedPlan));
        }
      } catch (error) {
        console.error('Error loading study plan:', error);
      }
    };

    loadStudyPlan();
  }, []);

  // Order user's subjects alphabetically - handle both string and object formats
  
  
  const orderedSubjects = user.subjects?.map(subject => {
    
    
    if (typeof subject === 'string') {
      // Check if it's a JSON string that needs parsing
      if (subject.startsWith('{') && subject.includes('subject_name')) {
        try {
          const parsedSubject = JSON.parse(subject);
  
          return parsedSubject.subject_name;
        } catch (error) {
          console.warn('🔧 Failed to parse JSON subject:', error);
          return subject; // Return original string if parsing fails
        }
      }
      return subject;
    } else if (subject && typeof subject === 'object' && subject.subject_name) {
      return subject.subject_name;
    } else {
      console.warn('🔧 Invalid subject format:', subject);
      return String(subject); // Fallback to string representation
    }
  }).filter(Boolean).sort() || [];
  
  
  


  // Calculate unselected subjects from database
  const unselectedSubjects = availableSubjects.filter(subject => !user.subjects.includes(subject.subject_name));
  


  // Function to add a subject to user's course selection
  const handleAddSubject = async (subjectName, bookName) => {
    setAddingSubject(subjectName);
    try {
      const updatedSubjects = [...user.subjects, subjectName];
      
      // Create updated subject data with book information
      const currentSubjectData = user.selectedSubjectData || [];
      const updatedSubjectData = [
        ...currentSubjectData,
        {
          subject_name: subjectName,
          book_name: bookName
        }
      ];
      
      // Save to database using Supabase
      const { data, error } = await supabase
        .from('course_selections')
        .upsert({
          user_id: authUser.id,
          selected_class: user.class,
          selected_stream: user.stream,
          selected_subjects: updatedSubjectData, // Save full subject data with book names
          full_name: user.name,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });
      
      if (!error) {
        setUser(prev => ({
          ...prev,
          subjects: updatedSubjects,
          selectedSubjectData: updatedSubjectData
        }));
        
        // Update localStorage for backward compatibility
        localStorage.setItem('user', JSON.stringify({
          name: user.name,
          class: user.class,
          stream: user.stream,
          subjects: updatedSubjects
        }));
        

      } else {
        console.error('Error saving to database:', error);
      }
    } catch (error) {
      console.error('Error adding subject:', error);
    } finally {
      setAddingSubject(null);
    }
  };

  const handleUpload = (subject) => {
    navigate(`/upload/${encodeURIComponent(subject)}`);
  };

  const handleStudy = (subjectName) => {
    // Find the specific book for this subject
    const userSelectedBook = user.selectedSubjectData?.find(s => 
      typeof s === 'object' && s.subject_name === subjectName
    );
    
    if (userSelectedBook && userSelectedBook.book_name) {
      // Navigate with both subject and book information
      const studyPath = `/study/${encodeURIComponent(subjectName)}/${encodeURIComponent(userSelectedBook.book_name)}`;
      navigate(studyPath);
    } else {
      // Fallback to subject-only navigation
      navigate(`/study/${encodeURIComponent(subjectName)}`);
    }
  };

  const handleFlashcards = async (subjectName) => {
    try {
      // Check for existing content first
      const existingContent = await apiService.getExistingContent('subject_flashcard', subjectName, '', 1, 5);
      
      if (existingContent.total > 0) {
        // Show existing content modal
        setExistingContentData(existingContent.data);
        setSelectedContentType('subject_flashcard');
        setSelectedSubject(subjectName);
        setShowExistingContentModal(true);
        return;
      }
      
      // No existing content, proceed with number input
      const userSelectedBook = user.selectedSubjectData?.find(s => 
        typeof s === 'object' && s.subject_name === subjectName
      );
      
      const bookInfo = userSelectedBook?.book_name ? ` (${userSelectedBook.book_name})` : '';
      
      setNumberInputConfig({
        title: `Generate ${subjectName}${bookInfo} Flashcards`,
        description: `Create flashcards distributed across all chapters of ${subjectName}${bookInfo}`,
        contentType: 'flashcards',
        defaultValue: 25,
        minValue: 5,
        maxValue: 25
      });
      setSelectedSubject(subjectName);
      setShowNumberInput(true);
    } catch (error) {
      console.error('Error checking existing content:', error);
      // Fallback to number input
      const userSelectedBook = user.selectedSubjectData?.find(s => 
        typeof s === 'object' && s.subject_name === subjectName
      );
      
      const bookInfo = userSelectedBook?.book_name ? ` (${userSelectedBook.book_name})` : '';
      
      setNumberInputConfig({
        title: `Generate ${subjectName}${bookInfo} Flashcards`,
        description: `Create flashcards distributed across all chapters of ${subjectName}${bookInfo}`,
        contentType: 'flashcards',
        defaultValue: 25,
        minValue: 5,
        maxValue: 25
      });
      setSelectedSubject(subjectName);
      setShowNumberInput(true);
    }
  };

  const handleQuiz = async (subjectName) => {
    try {
      // Check for existing content first
      const existingContent = await apiService.getExistingContent('subject_quiz', subjectName, '', 1, 5);
      
      if (existingContent.total > 0) {
        // Show existing content modal
        setExistingContentData(existingContent.data);
        setSelectedContentType('subject_quiz');
        setSelectedSubject(subjectName);
        setShowExistingContentModal(true);
        return;
      }
      
      // No existing content, proceed with number input
      const userSelectedBook = user.selectedSubjectData?.find(s => 
        typeof s === 'object' && s.subject_name === subjectName
      );
      
      const bookInfo = userSelectedBook?.book_name ? ` (${userSelectedBook.book_name})` : '';
      
      setNumberInputConfig({
        title: `Generate ${subjectName}${bookInfo} Quiz`,
        description: `Create quiz questions distributed across all chapters of ${subjectName}${bookInfo}`,
        contentType: 'quiz',
        defaultValue: 25,
        minValue: 5,
        maxValue: 25
      });
      setSelectedSubject(subjectName);
      setShowNumberInput(true);
    } catch (error) {
      console.error('Error checking existing content:', error);
      // Fallback to number input
      const userSelectedBook = user.selectedSubjectData?.find(s => 
        typeof s === 'object' && s.subject_name === subjectName
      );
      
      const bookInfo = userSelectedBook?.book_name ? ` (${userSelectedBook.book_name})` : '';
      
      setNumberInputConfig({
        title: `Generate ${subjectName}${bookInfo} Quiz`,
        description: `Create quiz questions distributed across all chapters of ${subjectName}${bookInfo}`,
        contentType: 'quiz',
        defaultValue: 25,
        minValue: 5,
        maxValue: 25
      });
      setSelectedSubject(subjectName);
      setShowNumberInput(true);
    }
  };

  const handleNumberInputConfirm = async (numItems) => {
    if (!selectedSubject) return;
    
    // Find the specific book for this subject
    const userSelectedBook = user.selectedSubjectData?.find(s => 
      typeof s === 'object' && s.subject_name === selectedSubject
    );
    
    const bookName = userSelectedBook?.book_name || '';
    
    setLoading(true);
    try {
      if (numberInputConfig.contentType === 'flashcards') {
        const response = await apiService.generateSubjectFlashcards(selectedSubject, numItems, bookName);
        setFlashcards(response.flashcards || []);
        setShowFlashcards(true);
      } else if (numberInputConfig.contentType === 'quiz') {
        const response = await apiService.generateSubjectQuiz(selectedSubject, numItems, bookName);
        setQuizQuestions(response.questions || []);
        setShowQuiz(true);
      }
    } catch (error) {
      console.error('Error generating content:', error);
      // You might want to show an error message to the user
    } finally {
      setLoading(false);
    }
  };

  const handleRelearnContent = (contentType, response, originalContent) => {
    if (contentType === 'flashcard') {
      setFlashcards(response.flashcards || []);
      setShowFlashcards(true);
    } else if (contentType === 'quiz') {
      setQuizQuestions(response.questions || []);
      setShowQuiz(true);
    }
    setShowRelearnCard(false);
  };

  const handleRegenerateContent = (contentType, response) => {
    if (contentType === 'flashcard') {
      setFlashcards(response.flashcards || []);
      setShowFlashcards(true);
    } else if (contentType === 'quiz') {
      setQuizQuestions(response.questions || []);
      setShowQuiz(true);
    }
    setShowRelearnCard(false);
  };

  const handleCloseRelearnCard = () => {
    setShowRelearnCard(false);
    setRelearnCardData({
      contentType: null,
      subject: null
    });
  };

  const handleSelectExistingContent = (content) => {
    if (selectedContentType === 'subject_flashcard') {
      setFlashcards(content.content.flashcards || []);
      setShowFlashcards(true);
    } else if (selectedContentType === 'subject_quiz') {
      setQuizQuestions(content.content.questions || []);
      setShowQuiz(true);
    }
    setShowExistingContentModal(false);
  };

  const handleGenerateNewContent = () => {
    setShowExistingContentModal(false);
    // Proceed with number input for new content
    const userSelectedBook = user.selectedSubjectData?.find(s => 
      typeof s === 'object' && s.subject_name === selectedSubject
    );
    
    const bookInfo = userSelectedBook?.book_name ? ` (${userSelectedBook.book_name})` : '';
    const contentType = selectedContentType === 'subject_flashcard' ? 'flashcards' : 'quiz';
    
    setNumberInputConfig({
      title: `Generate ${selectedSubject}${bookInfo} ${contentType === 'flashcards' ? 'Flashcards' : 'Quiz'}`,
      description: `Create ${contentType} distributed across all chapters of ${selectedSubject}${bookInfo}`,
      contentType: contentType,
      defaultValue: 25,
      minValue: 5,
      maxValue: 25
    });
    setShowNumberInput(true);
  };

  const handleCheckpointClick = () => {
    setShowCheckpoint(true);
  };

  const handleCheckpointComplete = (completed, total) => {
    setShowCheckpoint(false);
    // Update user coins based on completion
    if (completed === total && total > 0) {
      setUser(prev => ({ ...prev, coins: prev.coins + 10 }));
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="w-full flex flex-col items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container w-full flex flex-col items-center py-6 gap-6">
        {/* Welcome/User Stats Card - Compact Design */}
        <div className="w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="font-heading text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                Welcome, {user.name}! 👋
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-300 hidden sm:block">
                Ready to continue your learning journey?
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 border border-amber-200 dark:border-amber-800/50">
                <img src={themeIcons?.coin} alt={themeTexts?.coinLabel} className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-bold text-sm text-amber-800 dark:text-amber-200">
                  {user.coins} {themeTexts?.coinLabel}
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 border border-purple-200 dark:border-purple-800/50">
                <img src={themeIcons?.streak} alt={themeTexts?.streakLabel} className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-bold text-sm text-purple-800 dark:text-purple-200">
                  {user.streak} {themeTexts?.streakLabel}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Exam Countdown and Study Plan Integration */}
        {studyPlan && (
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ExamCountdown 
              studyPlan={studyPlan} 
              onCheckpointClick={handleCheckpointClick}
            />
            
            {/* Quick Actions Card */}
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
              <h3 className="text-lg font-heading font-bold text-gray-900 dark:text-white mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/planner')}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  📅 View Study Plan
                </button>
                <button
                  onClick={() => navigate('/calendar')}
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  📊 Study Calendar
                </button>
                <button
                  onClick={handleCheckpointClick}
                  className="w-full px-4 py-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800/50 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  ✅ Daily Checkpoint
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Selected Subjects Cards - Compact Grid */}
        {user.subjects && user.subjects.length > 0 && (
          <div className="w-full">
            <h2 className="text-lg sm:text-xl font-heading font-bold text-gray-900 dark:text-white mb-4">
              Your Subjects
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {orderedSubjects.map(subjectName => {
                // Find the specific book that the user selected for this subject
                const userSelectedBook = user.selectedSubjectData?.find(s => 
                  typeof s === 'object' && s.subject_name === subjectName
                );
                
                return (
                  <div
                    key={subjectName}
                    className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg rounded-lg shadow-sm border border-gray-200/50 dark:border-gray-700/50 transition-all duration-300 ease-in-out hover:shadow-lg hover:scale-105 hover:border-gray-300/70 dark:hover:border-gray-600/70 group transform"
                    tabIndex={0}
                  >
                    <div className="p-3">
                      {/* Row 1: Subject Name (Centered) */}
                      <div className="text-center mb-2">
                        <h3 className="font-semibold text-sm text-gray-900 dark:text-white leading-tight break-words">
                          {subjectName}
                        </h3>
                      </div>
                      
                      {/* Row 2: Book Name */}
                      {userSelectedBook && (
                        <div className="text-center mb-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-300">Book:</span>
                            <span className="text-gray-700 dark:text-gray-200 break-words ml-1">{userSelectedBook.book_name}</span>
                          </div>
                        </div>
                      )}
                      
                                              {/* Row 3: Action Buttons (Centered) */}
                        <div className="flex justify-center gap-1">
                          <Tooltip title="Study" arrow>
                            <button
                              onClick={() => handleStudy(subjectName)}
                              className="touch-target p-1.5 rounded-md hover:bg-teal-100 dark:hover:bg-teal-900/50 focus:bg-teal-200 dark:focus:bg-teal-800/50 transition-all duration-200 ease-in-out hover:scale-110 transform"
                              aria-label={`Study ${subjectName}`}
                            >
                              <MenuBookIcon className="text-teal-600 dark:text-teal-300 w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Quiz" arrow>
                            <button
                              onClick={() => handleQuiz(subjectName)}
                              className="touch-target p-1.5 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 focus:bg-blue-200 dark:focus:bg-blue-800/50 transition-all duration-200 ease-in-out hover:scale-110 transform"
                              aria-label={`Quiz for ${subjectName}`}
                            >
                              <QuizIcon className="text-blue-600 dark:text-blue-300 w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Flashcards" arrow>
                            <button
                              onClick={() => handleFlashcards(subjectName)}
                              className="touch-target p-1.5 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/50 focus:bg-purple-200 dark:focus:bg-purple-800/50 transition-all duration-200 ease-in-out hover:scale-110 transform"
                              aria-label={`Flashcards for ${subjectName}`}
                            >
                              <StyleIcon className="text-purple-600 dark:text-purple-300 w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Upload (Coming Soon)" arrow>
                            <button
                              disabled
                              className="touch-target p-1.5 rounded-md bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-50 transition-all duration-200 ease-in-out"
                              aria-label={`Upload for ${subjectName} (Coming Soon)`}
                            >
                              <UploadFileIcon className="text-gray-400 dark:text-gray-500 w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Unselected Subjects - Compact Collapsible */}
        {(unselectedSubjects.length > 0 || true) && (
          <div className="w-full">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg sm:text-xl font-heading font-bold text-gray-900 dark:text-white">
                Available Subjects {unselectedSubjects.length === 0 ? '(No unselected subjects)' : ''}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowUnselected(!showUnselected)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  <span>{showUnselected ? 'Hide' : 'Show'} ({unselectedSubjects.length})</span>
                  {showUnselected ? (
                    <ExpandLessIcon className="w-4 h-4" />
                  ) : (
                    <ExpandMoreIcon className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setShowUnselected(true)}
                  className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800/50"
                >
                  Force Show
                </button>
              </div>
            </div>

                        {showUnselected && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {unselectedSubjects.length > 0 ? (
                  unselectedSubjects.map(subject => (
                    <div
                      key={`${subject.subject_name}-${subject.book_name}`}
                      className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-lg shadow-sm border border-gray-200/50 dark:border-gray-700/50 transition-all duration-300 ease-in-out hover:shadow-lg hover:scale-105 hover:border-gray-300/70 dark:hover:border-gray-600/70 transform"
                    >
                      <div className="p-3">
                        {/* Row 1: Subject Name (Centered) */}
                        <div className="text-center mb-2">
                          <h3 className="font-semibold text-sm text-gray-900 dark:text-white leading-tight break-words">
                            {subject.subject_name}
                          </h3>
                        </div>
                        
                        {/* Row 2: Book Name */}
                        <div className="text-center mb-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-300">Book:</span>
                            <span className="text-gray-700 dark:text-gray-200 break-words ml-1">{subject.book_name}</span>
                          </div>
                        </div>
                        
                        {/* Row 3: Add Button (Centered) */}
                        <div className="flex justify-center">
                          <button
                            onClick={() => handleAddSubject(subject.subject_name, subject.book_name)}
                            disabled={addingSubject === subject.subject_name}
                            className="touch-target p-1.5 rounded-md bg-teal-100 dark:bg-teal-900/50 hover:bg-teal-200 dark:hover:bg-teal-800/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out hover:scale-110 transform"
                            aria-label={`Add ${subject.subject_name} to your subjects`}
                          >
                            {addingSubject === subject.subject_name ? (
                              <div className="w-4 h-4 border-2 border-teal-600 dark:border-teal-300 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <AddIcon className="w-4 h-4 text-teal-600 dark:text-teal-300" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No unselected subjects available</p>
                    <p className="text-sm mt-2">All subjects for your class have been selected</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}



        {/* Empty State - Compact */}
        {(!user.subjects || user.subjects.length === 0) && (
          <div className="w-full text-center py-8">
            <div className="max-w-sm mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-teal-100 to-blue-100 dark:from-teal-900/30 dark:to-blue-900/30 rounded-full flex items-center justify-center">
                <MenuBookIcon className="w-8 h-8 text-teal-600 dark:text-teal-300" />
              </div>
              <h2 className="text-lg sm:text-xl font-heading font-bold text-gray-900 dark:text-white mb-2">
                No subjects selected yet
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                Choose your subjects to start your personalized learning journey
              </p>
              <button
                onClick={() => setShowUnselected(true)}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
              >
                Select Subjects
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <NumberInputModal
        isOpen={showNumberInput}
        onClose={() => setShowNumberInput(false)}
        onConfirm={handleNumberInputConfirm}
        title={numberInputConfig.title}
        description={numberInputConfig.description}
        contentType={numberInputConfig.contentType}
        defaultValue={numberInputConfig.defaultValue}
        minValue={numberInputConfig.minValue}
        maxValue={numberInputConfig.maxValue}
      />

      {selectedSubject && (
        <>
          <FlashcardsModal
            isOpen={showFlashcards}
            onClose={() => setShowFlashcards(false)}
            subject={selectedSubject}
            chapter="All Chapters"
            flashcards={flashcards}
            isExisting={flashcards.length > 0}
            onRegenerate={() => handleRegenerateContent('flashcard')}
          />
          <QuizModal
            isOpen={showQuiz}
            onClose={() => setShowQuiz(false)}
            subject={selectedSubject}
            chapter="All Chapters"
            questions={quizQuestions}
            isExisting={quizQuestions.length > 0}
          />
        </>
      )}

      {/* Existing Content Modal */}
      <ExistingContentModal
        isOpen={showExistingContentModal}
        onClose={() => setShowExistingContentModal(false)}
        existingContent={existingContentData}
        subject={selectedSubject}
        chapter="All Chapters"
        onSelectContent={handleSelectExistingContent}
        onGenerateNew={handleGenerateNewContent}
      />

      {/* Relearn Content Card */}
      {showRelearnCard && relearnCardData.contentType && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-2xl">
            <RelearnContentCard
              subject={relearnCardData.subject}
              chapter="All Chapters"
              contentType={relearnCardData.contentType}
              onRelearn={handleRelearnContent}
              onRegenerate={handleRegenerateContent}
              onClose={handleCloseRelearnCard}
            />
          </div>
        </div>
      )}

      {/* Checkpoint System */}
      <AnimatePresence>
        {showCheckpoint && (
          <CheckpointSystem
            studyPlan={studyPlan}
            onComplete={handleCheckpointComplete}
          />
        )}
      </AnimatePresence>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl max-w-md w-full mx-4">
            <div className="flex flex-col items-center space-y-4">
              {/* Skeleton for loading content */}
              <div className="relative overflow-hidden w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              
              {/* Skeleton text lines */}
              <div className="space-y-2 w-full">
                <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
                <div className="relative overflow-hidden h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 